import {
  MappingJobBackendStatus,
  MappingViewStatus,
  MappingWebSocketMessageType,
  type IMappingJobStatusResponse,
  type IMappingJobViewModel,
  type IMappingNotificationMessage,
} from "@models/mapping";

/**
 * Collapse the mapping backend's lifecycle status into the three states the
 * extraction UI renders. AwaitingUpload / Parsing / Running all read as
 * "running" (work is in flight); Completed / Failed are terminal.
 */
export const mapMappingBackendStatus = (status: string): MappingViewStatus => {
  switch (status) {
    case MappingJobBackendStatus.Completed:
      return MappingViewStatus.Completed;
    case MappingJobBackendStatus.Failed:
      return MappingViewStatus.Failed;
    case MappingJobBackendStatus.AwaitingUpload:
    case MappingJobBackendStatus.Parsing:
    case MappingJobBackendStatus.Running:
    default:
      return MappingViewStatus.Running;
  }
};

/**
 * Map a `mapping.job.{progress,completed,failed}` WS message type to the
 * view status. Returns null for non-job frames (auth/ping/pong).
 */
const messageTypeToStatus = (type: string): MappingViewStatus | null => {
  switch (type) {
    case MappingWebSocketMessageType.Progress:
      return MappingViewStatus.Running;
    case MappingWebSocketMessageType.Completed:
      return MappingViewStatus.Completed;
    case MappingWebSocketMessageType.Failed:
      return MappingViewStatus.Failed;
    default:
      return null;
  }
};

/**
 * Build the per-item view model from a REST `/jobs?process_item_id=` row.
 * `processItemId` is passed through because the status row is correlated to
 * the extraction result by the caller, not echoed by the backend.
 */
export const buildMappingJobViewModelFromStatus = (
  response: IMappingJobStatusResponse,
  processItemId: string,
): IMappingJobViewModel => ({
  jobId: response.job_id,
  processId: "",
  processItemId,
  status: mapMappingBackendStatus(response.status),
  rowsDone: response.rows_done ?? 0,
  rowsTotal: response.rows_total ?? 0,
  service: response.service ?? null,
  pluginId: response.plugin_id ?? null,
});

/**
 * Fold a live WS notification onto the previous view model. Progress frames
 * often omit fields the initial REST fetch already supplied (job_id, totals),
 * so unset values fall back to `prev` rather than clobbering it.
 */
export const applyMappingMessage = (
  prev: IMappingJobViewModel | null,
  message: IMappingNotificationMessage,
): IMappingJobViewModel => {
  const status =
    messageTypeToStatus(message.type) ?? prev?.status ?? MappingViewStatus.Running;
  return {
    jobId: message.job_id ?? prev?.jobId ?? "",
    processId: message.process_id ?? prev?.processId ?? "",
    processItemId: message.process_item_id ?? prev?.processItemId ?? "",
    status,
    rowsDone: message.rows_done ?? prev?.rowsDone ?? 0,
    rowsTotal: message.rows_total ?? prev?.rowsTotal ?? 0,
    service: message.service ?? prev?.service ?? null,
    pluginId: message.plugin_id ?? prev?.pluginId ?? null,
  };
};
